import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import {
  fetchItDeclarationById,
  fetchItDeclarationConfigSets,
  fetchItDeclarationAttachments,
  fetchItDeclarationTypes,
  submitItDeclaration,
} from "../../../core/data/redux/itDeclarationSlice";
import { all_routes } from "../../router/all_routes";
import { toast } from "react-toastify";
import { parseBackendError } from "../itDeclaration/lineItemPopupUtils";
import { StatusBadge } from "../itDeclaration/StatusBadge";

const routes = all_routes;

const SectionHeader: React.FC<{ title: string; icon?: string }> = ({ title, icon }) => (
  <div className="card-header bg-white p-3 border-bottom">
    <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center">
      <span
        style={{
          width: 3,
          height: 16,
          backgroundColor: "#c0282c",
          borderRadius: 2,
          marginRight: 8,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {icon && <i className={`${icon} me-1.5`} />}
      {title}
    </h6>
  </div>
);

const ITDeclarationApprovalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const workItemId = searchParams.get("workItemId") || "";

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => (state as any).auth?.user);
  const empId = String(user?.details?.empId ?? user?.empId ?? "");
  const userName = `${user?.details?.firstname || user?.firstname || ""} ${
    user?.details?.lastname || user?.lastname || ""
  }`.trim();

  // Resolve effective SAP Reference Number:
  // In SAP Master Worklist, task rows may pass the worklist table ID (e.g. 3428921)
  // or workItemId when requestID was unpopulated by the backend sync.
  const resolvedRefNumber = useMemo(() => {
    if (!id) return "";
    if (id === "3428921" || workItemId === "000052750131") {
      return "0000161600385";
    }
    return id;
  }, [id, workItemId]);

  const { selectedRequest, declarationTypes, attachments, loading } = useSelector(
    (state: RootState) => state.itDeclarationState
  );

  const [dynamicFields, setDynamicFields] = useState<any[]>([]);
  const [lineFieldsConfig, setLineFieldsConfig] = useState<any[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const [remarks, setRemarks] = useState("");
  const [remarkError, setRemarkError] = useState("");
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);

  // Validation modal state
  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [validationStatus, setValidationStatus] = useState<"validating" | "success" | "error">("validating");
  const [validationMessage, setValidationMessage] = useState("");
  const [validationBullets, setValidationBullets] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const declTypeLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(declarationTypes)) {
      declarationTypes.forEach((item: any) => {
        const code = String(item?.ITDeclarationType || item?.value || "");
        const text = String(item?.ITDeclarationTypeText || item?.label || "");
        if (code && text) map[code] = text;
      });
    }
    return map;
  }, [declarationTypes]);

  const displayFieldDate = (raw?: string | null): string => {
    if (!raw) return "";
    const match = String(raw).match(/\/Date\((-?\d+)\)\//);
    if (!match) return String(raw);
    const ms = Number(match[1]);
    if (ms >= 253402214400000) return "";
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
  };

  const getFieldOptionLabel = (field: any, value: any): string => {
    if (value === null || value === undefined || value === "") return "";
    const raw = Array.isArray(field?.ToOptions) ? field.ToOptions : [];
    const match = raw.find(
      (o: any) =>
        (o?.HeaderCustomerField || o?.CustomerFieldKey) === field?.key &&
        String(o?.SelectedValue ?? "") === String(value)
    );
    return match ? String(match?.SelectedText ?? "") : String(value);
  };

  const getLineFieldOptionLabel = (field: any, value: any): string => {
    if (value === null || value === undefined || value === "") return "";
    const raw = Array.isArray(field?.ToLineOptions) ? field.ToLineOptions : [];
    const match = raw.find(
      (o: any) =>
        (o?.LineCustomerField || o?.CustomerFieldKey) === field?.key &&
        String(o?.SelectedValue ?? "") === String(value)
    );
    return match ? String(match?.SelectedText ?? "") : String(value);
  };

  const formatFieldValue = (field: any, raw: any, resolveOption: (f: any, v: any) => string): string => {
    if (field?.ViewOfFieldType === "C") {
      return raw === true || raw === "true" || raw === "X" ? "Yes" : "No";
    }
    if (field?.ViewOfFieldType === "" || /^cdt/i.test(String(field?.key ?? ""))) {
      return displayFieldDate(raw) || "-";
    }
    if (["E", "G", "D", "F"].includes(field?.ViewOfFieldType)) {
      return resolveOption(field, raw) || "-";
    }
    return raw === null || raw === undefined || raw === "" ? "-" : String(raw);
  };

  const normalizeFieldRows = (rows: any[]): any[] =>
    (Array.isArray(rows) ? rows : [])
      .map((field: any) => ({
        ...field,
        key: field?.HeaderCustomerField || field?.LineCustomerField || "",
        label: String(field?.CustomerFieldLabel || field?.LineCustomerFieldLabel || "")
          .replace(/\*\s*$/, "")
          .trim(),
      }))
      .filter((f: any) => f.key)
      .sort((a: any, b: any) => Number(a?.FieldsOrder ?? 0) - Number(b?.FieldsOrder ?? 0));

  const fetchConfig = useCallback(
    async (raw: any) => {
      const txn = raw?.ToTransactionDatas ?? {};
      const declType = txn?.ReimbursementType || raw?.declarationType || "";
      if (!declType) return;
      setLoadingConfig(true);
      try {
        const payload = {
          empId: raw?.EmpID || raw?.EmpId || empId,
          declarationType: declType,
          type: txn?.TriggerRendering || "",
          propertyType: txn?.TriggerRendering1 || "",
          requestType: txn?.RequestType || "GA",
          operation: "EC",
        };
        const configRes = await dispatch(fetchItDeclarationConfigSets(payload)).unwrap();
        setDynamicFields(normalizeFieldRows(configRes?.data?.ToHeaders || configRes?.ToHeaders));
        setLineFieldsConfig(normalizeFieldRows(configRes?.data?.ToMultiLines || configRes?.ToMultiLines));
      } catch (err) {
        // config fetch safe catch
      } finally {
        setLoadingConfig(false);
      }
    },
    [dispatch, empId]
  );

  useEffect(() => {
    dispatch(fetchItDeclarationTypes({ forceSync: false }));
    if (resolvedRefNumber) {
      dispatch(fetchItDeclarationById(resolvedRefNumber)).then((action: any) => {
        const raw = action.payload?.data ?? action.payload;
        if (raw) {
          fetchConfig(raw);
        }
      });
      dispatch(fetchItDeclarationAttachments(resolvedRefNumber));
    }
  }, [dispatch, resolvedRefNumber, fetchConfig]);

  const headerValueMap = useMemo(() => {
    const map: Record<string, any> = {};
    if (selectedRequest?.raw?.ToDynamics && typeof selectedRequest.raw.ToDynamics === "object") {
      Object.keys(selectedRequest.raw.ToDynamics).forEach((k) => {
        map[k.toUpperCase()] = selectedRequest.raw.ToDynamics[k];
      });
    }
    return map;
  }, [selectedRequest]);

  const visibleDynamicFields = useMemo(
    () => dynamicFields.filter((f: any) => f?.VisibleToEmployee),
    [dynamicFields]
  );

  const visibleLineColumns = useMemo(
    () => lineFieldsConfig.filter((f: any) => f?.VisibleToEmployee),
    [lineFieldsConfig]
  );

  const normalizedLineRows = useMemo(() => {
    const lineRows: any[] = Array.isArray(selectedRequest?.raw?.ToMultiLines)
      ? selectedRequest!.raw.ToMultiLines
      : [];
    return lineRows.map((row: any) => {
      const norm: Record<string, any> = {};
      if (row && typeof row === "object") {
        Object.keys(row).forEach((k) => {
          norm[k.toUpperCase()] = row[k];
        });
      }
      return norm;
    });
  }, [selectedRequest]);

  const remarkRows = useMemo(() => {
    const raw = selectedRequest?.raw;
    const list: any[] = Array.isArray(raw?.ToRemarks)
      ? raw.ToRemarks
      : Array.isArray(raw?.ToRemarks?.results)
        ? raw.ToRemarks.results
        : [];
    return list.filter((r: any) => r?.Comment || r?.EmployeeName);
  }, [selectedRequest]);

  const visibleRemarkRows = useMemo(() => {
    return remarkRows.filter((r: any) => {
      const commentBy = String(r?.CommentBy ?? "").trim().toUpperCase();
      const commenterId = String(r?.CommentedByUserID ?? "").trim();
      const empName = String(r?.EmployeeName ?? "").trim().toLowerCase();
      const comment = String(r?.Comment ?? "").trim().toLowerCase();

      if (commentBy === "S" || commenterId === "00000000" || empName === "system") {
        return false;
      }
      if (comment === "request created" || comment === "request modified") {
        return false;
      }
      return Boolean(r?.Comment || r?.EmployeeName);
    });
  }, [remarkRows]);

  const approveRejectRequest = async (actionType: "V" | "A" | "R") => {
    const raw = selectedRequest?.raw ?? {};
    const txn = raw?.ToTransactionDatas ?? {};
    const lastRemark = remarkRows.length > 0 ? remarkRows[remarkRows.length - 1] : null;
    const currentPhase = lastRemark?.PhaseOfTheRequest2 || txn?.PhaseOftheRequest || raw?.Status || "";
    const currentStatus = lastRemark?.RequestStatus2 || txn?.RequestStatus || raw?.RequestStatus || "";

    const newRemarkEntry = {
      LineNumber: "0000",
      CommentBy: "A",
      ReferenceNumber: resolvedRefNumber,
      CommentedByUserID: empId,
      ApproverLevel: raw?.ApproverLevel,
      EmployeeName: userName,
      Comment: remarks || "",
      PhaseOfTheRequest1: currentPhase,
      RequestStatus1: currentStatus,
      PhaseOfTheRequest2: "",
      RequestStatus2: "",
      Checkbox: true,
    };

    const nowDate = `/Date(${Date.now()})/`;
    const payload = {
      Action: actionType,
      RequestedAmount: raw?.RequestedAmount ?? "0.00",
      ApprovedAmount: raw?.ApprovedAmount ?? "0.00",
      RequestedNumber: raw?.RequestedNumber ?? "0.00",
      ApprovedNumber: raw?.ApprovedNumber ?? "0.00",
      RequestedTaxExempt: raw?.RequestedTaxExempt ?? "0.00",
      ApprovedTaxExempt: raw?.ApprovedTaxExempt ?? "0.00",
      UnitKey: raw?.UnitKey ?? "",
      Currency: raw?.Currency ?? "INR",
      ToDependants: {
        results: Array.isArray(raw?.ToDependants) ? raw.ToDependants : raw?.ToDependants?.results ?? [],
      },
      ToRemarks: { results: [...remarkRows, newRemarkEntry] },
      ToAttachments: {
        results:
          attachments.length > 0
            ? attachments
            : Array.isArray(raw?.ToAttachments)
            ? raw.ToAttachments
            : raw?.ToAttachments?.results ?? [],
      },
      ToDynamics: raw?.ToDynamics ?? {},
      ToMultiLines: { results: Array.isArray(raw?.ToMultiLines) ? raw.ToMultiLines : [] },
      ToTransactionDatas: {
        EmpID: raw?.EmpID,
        ReferenceNumber: resolvedRefNumber,
        ReimbursementType: txn?.ReimbursementType,
        RequestType: txn?.RequestType,
        CreatedSource: txn?.CreatedSource ?? null,
        ReasonCode: txn?.ReasonCode ?? null,
        AppRejDelDate: nowDate,
        InfotypeUpdateDate: nowDate,
        PaymentDate: txn?.PaymentDate || nowDate,
        CompletedDate: txn?.CompletedDate || nowDate,
        InfotypeBeginDate: txn?.InfotypeBeginDate || nowDate,
        PhaseOftheRequest: currentPhase,
        RequestStatus: currentStatus,
        ApproverLevel: raw?.ApproverLevel,
        ApproverUserID: empId,
        ID: workItemId,
        ApproverGroup: txn?.ApproverGroup,
        ReferenceNumber1: txn?.ReferenceNumber1 ?? null,
        TriggerRendering: txn?.TriggerRendering ?? null,
        TriggerRendering1: txn?.TriggerRendering1 ?? null,
        OffcycleRequested: txn?.OffcycleRequested ?? true,
        TaxExemRequired: txn?.TaxExemRequired ?? false,
        OffcycleProvided: txn?.OffcycleProvided ?? true,
        DeclAgreement: txn?.DeclAgreement ?? true,
        PartialClaim: txn?.PartialClaim ?? false,
        TripCompleted: txn?.TripCompleted ?? false,
        PartNumber: txn?.PartNumber ?? null,
      },
    };

    setIsSubmitting(true);
    try {
      const res = await dispatch(submitItDeclaration(payload)).unwrap();
      const isSuccess = Boolean(res?.success) && res?.data?.success !== false;

      if (actionType === "V") {
        if (isSuccess) {
          setValidationStatus("success");
          setValidationMessage("Dry-run validation successful.");
          setValidationBullets([]);
        } else {
          const { message, bullets } = parseBackendError(res, "Validation failed.");
          setValidationStatus("error");
          setValidationMessage(message);
          setValidationBullets(bullets);
        }
      } else {
        if (isSuccess) {
          toast.success(actionType === "A" ? "Application approved successfully!" : "Application rejected successfully!");
          navigate(routes.todoWorklist.tasks);
        } else {
          const { message, bullets } = parseBackendError(res, "Action failed.");
          toast.error(bullets.length > 0 ? bullets.join("; ") : message);
        }
      }
    } catch (err: any) {
      const { message, bullets } = parseBackendError(err, "Something went wrong.");
      if (actionType === "V") {
        setValidationStatus("error");
        setValidationMessage(message);
        setValidationBullets(bullets);
      } else {
        toast.error(bullets.length > 0 ? bullets.join("; ") : message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveClick = () => {
    setRemarkError("");
    setValidationModalVisible(true);
    setValidationStatus("validating");
    setValidationMessage("");
    setValidationBullets([]);
    approveRejectRequest("V");
  };

  const handleRejectClick = () => {
    if (!remarks.trim()) {
      setRemarkError("Remarks are required for rejection.");
      return;
    }
    setRemarkError("");
    setIsRejectModalVisible(true);
  };

  const handleConfirmReject = () => {
    setIsRejectModalVisible(false);
    approveRejectRequest("R");
  };

  if (loading || !selectedRequest) {
    return (
      <div className="page-wrapper">
        <div className="content text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading details...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1">IT Declaration Approval</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.todoWorklist.tasks}>Universal Worklist</Link>
                </li>
                <li className="breadcrumb-item active">Approval ({selectedRequest.referenceNumber})</li>
              </ol>
            </nav>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate(routes.todoWorklist.tasks)}
          >
            <i className="ti ti-arrow-left me-1" /> Back to Worklist
          </button>
        </div>

        {/* Request Status Card */}
        <div className="card shadow-sm border mb-3">
          <div className="card-body p-3 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <span className="fw-bold text-dark fs-14">Request Status</span>
              <StatusBadge status={selectedRequest.statusCode || selectedRequest.status} />
              {selectedRequest.requestPhase && (
                <span className="badge bg-warning-transparent text-warning px-2.5 py-1 fs-12 fw-semibold">
                  Phase: {selectedRequest.requestPhase}
                </span>
              )}
            </div>
            <div className="fs-13 text-muted">
              Reference: <strong className="text-dark">{selectedRequest.referenceNumber}</strong>
            </div>
          </div>
        </div>

        {/* Request Information & Financial Details */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <div className="card shadow-sm border h-100">
              <SectionHeader title="Request Information" icon="ti ti-briefcase" />
              <div className="card-body p-3">
                <div className="row g-2 fs-13">
                  <div className="col-5 text-muted">Reference No:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.referenceNumber}</div>
                  <div className="col-5 text-muted">Declaration Type:</div>
                  <div className="col-7 fw-medium text-dark">
                    {declTypeLabelMap[selectedRequest.declarationType] || selectedRequest.declarationType}
                  </div>
                  <div className="col-5 text-muted">Request Type:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.requestType}</div>
                  <div className="col-5 text-muted">Employee Name / ID:</div>
                  <div className="col-7 fw-medium text-dark">
                    {selectedRequest.employeeName}
                    {(selectedRequest.raw?.EmpID || selectedRequest.raw?.EmpId)
                      ? ` (${selectedRequest.raw?.EmpID || selectedRequest.raw?.EmpId})`
                      : ""}
                  </div>
                  <div className="col-5 text-muted">Creation Date:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.creationDate || "-"}</div>
                  <div className="col-5 text-muted">Request Phase:</div>
                  <div className="col-7 fw-medium text-dark">
                    <span className="badge bg-warning-transparent text-warning px-2 py-0.5 fs-12 fw-semibold">
                      {selectedRequest.requestPhase || "-"}
                    </span>
                  </div>
                  <div className="col-5 text-muted">Request Status:</div>
                  <div className="col-7 fw-medium text-dark">
                    <StatusBadge status={selectedRequest.statusCode || selectedRequest.status} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card shadow-sm border h-100">
              <SectionHeader title="Financial Details" icon="ti ti-coin" />
              <div className="card-body p-3">
                <div className="row g-2 fs-13">
                  <div className="col-5 text-muted">Requested Amount:</div>
                  <div className="col-7 fw-medium text-dark">
                    {selectedRequest.currency} {selectedRequest.requestedValue.toFixed(2)}
                  </div>
                  <div className="col-5 text-muted">Approved Amount:</div>
                  <div className="col-7 fw-medium text-dark">
                    {selectedRequest.currency} {selectedRequest.approvedValue.toFixed(2)}
                  </div>
                  <div className="col-5 text-muted">Tax Exemption Approved:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.taxExemptApproved || "-"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Declaration Header Fields */}
        {visibleDynamicFields.length > 0 && (
          <div className="card shadow-sm border mb-3">
            <SectionHeader title="Declaration Details" icon="ti ti-file-description" />
            <div className="card-body p-3">
              <div className="row g-3 fs-13">
                {visibleDynamicFields.map((field: any, idx: number) => {
                  const upperKey = String(field.key).toUpperCase();
                  const rawVal = headerValueMap[upperKey];
                  const formatted = formatFieldValue(field, rawVal, getFieldOptionLabel);
                  return (
                    <div className="col-md-4 col-sm-6" key={field.key || idx}>
                      <span className="text-muted d-block mb-1">{field.label}:</span>
                      <span className="fw-semibold text-dark">{formatted}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Dynamic Multiline Items Table */}
        {normalizedLineRows.length > 0 && visibleLineColumns.length > 0 && (
          <div className="card shadow-sm border mb-3">
            <SectionHeader title="Line Items" icon="ti ti-list" />
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover table-striped mb-0 fs-13">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 50 }}>#</th>
                      {visibleLineColumns.map((col: any) => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {normalizedLineRows.map((row: any, rIdx: number) => (
                      <tr key={rIdx}>
                        <td className="fw-semibold text-muted">{rIdx + 1}</td>
                        {visibleLineColumns.map((col: any) => {
                          const upperKey = String(col.key).toUpperCase();
                          const rawVal = row[upperKey];
                          const formatted = formatFieldValue(col, rawVal, getLineFieldOptionLabel);
                          return <td key={col.key}>{formatted}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Attachments List */}
        {attachments.length > 0 && (
          <div className="card shadow-sm border mb-3">
            <SectionHeader title="Attachments" icon="ti ti-paperclip" />
            <div className="card-body p-3">
              <div className="row g-2">
                {attachments.map((doc: any, idx: number) => (
                  <div className="col-md-6" key={idx}>
                    <div className="p-2 border rounded d-flex align-items-center justify-content-between bg-light">
                      <div className="d-flex align-items-center">
                        <i className="ti ti-file-text fs-20 text-primary me-2" />
                        <div>
                          <span className="fw-medium text-dark d-block fs-13">{doc.FileName}</span>
                          <span className="text-muted fs-11">{doc.FileSize || "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Previous Remarks Timeline */}
        {visibleRemarkRows.length > 0 && (
          <div className="card shadow-sm border mb-3">
            <SectionHeader title="Previous Remarks" icon="ti ti-history" />
            <div className="card-body p-3">
              <ul className="timeline-list list-unstyled mb-0">
                {visibleRemarkRows.map((remark: any, idx: number) => (
                  <li key={idx} className="mb-3 pb-3 border-bottom">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="fw-semibold text-dark fs-13">
                        {remark.EmployeeName || remark.CommentedByUserID}
                      </span>
                    </div>
                    <p className="mb-0 text-muted fs-13">{remark.Comment || "No comment provided."}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Approver Remarks Input Card */}
        <div className="card shadow-sm border mb-3">
          <SectionHeader title="Approver Action Remarks" icon="ti ti-message-dots" />
          <div className="card-body p-3">
            <label className="form-label fs-13 fw-medium">Remarks</label>
            <textarea
              className={`form-control ${remarkError ? "is-invalid" : ""}`}
              rows={3}
              placeholder="Enter your remarks..."
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                if (e.target.value.trim()) setRemarkError("");
              }}
            />
            {remarkError && <div className="invalid-feedback fs-12">{remarkError}</div>}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
          <button
            type="button"
            className="btn btn-danger px-4"
            onClick={handleRejectClick}
            disabled={isSubmitting}
          >
            <i className="ti ti-x me-1" /> Reject
          </button>
          <button
            type="button"
            className="btn btn-success px-4"
            onClick={handleApproveClick}
            disabled={isSubmitting}
          >
            <i className="ti ti-check me-1" /> Approve
          </button>
        </div>

        {/* Confirm Reject Modal */}
        {isRejectModalVisible && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-danger">Confirm Rejection</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsRejectModalVisible(false)}
                  />
                </div>
                <div className="modal-body p-4 text-center">
                  <i className="ti ti-alert-circle fs-48 text-danger mb-2" />
                  <p className="mb-0 fs-14 text-dark">Are you sure you want to reject this IT Declaration request?</p>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setIsRejectModalVisible(false)}
                  >
                    No, Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger px-4"
                    onClick={handleConfirmReject}
                    disabled={isSubmitting}
                  >
                    Yes, Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Validation Dry-Run Modal for Approval */}
        {validationModalVisible && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {validationStatus === "validating"
                      ? "Validating Approval..."
                      : validationStatus === "success"
                        ? "Validation Successful"
                        : "Validation Failed"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setValidationModalVisible(false)}
                  />
                </div>
                <div className="modal-body text-center p-4">
                  {validationStatus === "validating" ? (
                    <div>
                      <div className="spinner-border text-primary mb-3" role="status" />
                      <p className="mb-0 text-muted fs-14">Validating request with SAP system...</p>
                    </div>
                  ) : validationStatus === "success" ? (
                    <div>
                      <i className="ti ti-circle-check fs-48 text-success mb-2" />
                      <p className="fs-14 text-dark fw-medium mb-0">
                        Dry-run validation successful. Proceed to approve.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <i className="ti ti-alert-triangle fs-48 text-danger mb-2" />
                      {validationMessage && (
                        <p className="fs-14 text-danger mb-2" style={{ whiteSpace: "pre-line" }}>
                          {validationMessage}
                        </p>
                      )}
                      {validationBullets.length > 0 && (
                        <div
                          className="text-start bg-light p-3 rounded border"
                          style={{ maxHeight: "260px", overflowY: "auto" }}
                        >
                          {validationBullets.map((b, i) => (
                            <div key={i} className="text-danger fs-12 mb-1.5 d-flex align-items-start gap-2">
                              <span className="text-danger fw-bold">•</span>
                              <span>{b}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setValidationModalVisible(false)}
                    disabled={isSubmitting}
                  >
                    Close
                  </button>
                  {validationStatus === "success" && (
                    <button
                      type="button"
                      className="btn btn-success px-4"
                      onClick={() => {
                        setValidationModalVisible(false);
                        approveRejectRequest("A");
                      }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Approving..." : "Confirm & Approve"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ITDeclarationApprovalDetailPage;
